import { FC, useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import {
  currentAttendeeEventsSidebarSelector,
  showAttendeeEventsSidebarSelector,
} from 'src/redesign/features/AttendeeEventsSidebar/store/selectors';
import { Sidebar, SidebarPassThroughOptions } from 'primereact/sidebar';
import { attendeeEventsSidebarActions } from 'src/redesign/features/AttendeeEventsSidebar/store/reducer';
import S from './AttendeeEventsSidebar.module.scss';
import { Text } from '@components/Text';
import calendarIcon from 'src/redesign/assets/calendar-icon.svg';
import timeIcon from 'src/redesign/assets/time-icon.svg';
import locationIcon from 'src/redesign/assets/location-icon.svg';
import { RedesignButton } from '@components/RedesignButton';
import {
  DownloadIcon,
  ExternalLinkIcon,
  EyeIcon,
  SendIcon,
  TicketIcon,
} from 'src/redesign/assets/icons';
import { Tag } from 'primereact/tag';
import c from 'clsx';
import styles from 'src/redesign/features/AttendeesSidebar/views/OrderSidebarView/components/OrderCard/OrderCard.module.scss';
import { Divider } from 'primereact/divider';
import { getPaymentLogo } from 'src/redesign/shared/utils/getPaymentLogo';
import {
  cancelAndRefundAttendee,
  fetchCartRequests,
  fetchTicketPdf,
  fetchTicketReceiptPdf,
  transferAttendeeTicket,
} from 'src/redesign/features/Attendees/store/actions';
import {
  cartRequestsLoadingSelector,
  cartRequestsSelector,
} from 'src/redesign/features/Attendees/store/selectors';
import { setCurrentModalAction } from 'src/redesign/features/Modals/store/reducer';
import { CONNECTED_MODALS } from 'src/redesign/features/Modals/store/constants';
import { toastNotificationsSlice } from 'src/redesign/features/Toast/store/reducer';
import moment from 'moment';
import { Dialog } from 'primereact/dialog';
import { Carousel } from 'primereact/carousel';
import appleStoreIcon from 'src/redesign/assets/apple-store-icon.svg';
import playStoreIcon from 'src/redesign/assets/play-store-icon.svg';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Ticket } from 'src/types/types';
import { useHistory, useLocation } from 'react-router-dom';
import { useCheckAttendeeEventsSidebarUrl } from 'src/redesign/features/AttendeeEventsSidebar/hooks/useCheckAttendeeEventsSidebarUrl';

const pt: SidebarPassThroughOptions = {
  root: {
    className: S.attendeeEventsSidebar,
  },
  header: {
    className: S.sidebarHeader,
  },
  content: {
    style: {
      padding: 0,
    },
  },
};

const RequestStatus = () => {
  const cartRequests = useAppSelector(cartRequestsSelector);
  const cartRequestsLoading = useAppSelector(cartRequestsLoadingSelector);

  if (!cartRequests || cartRequests.length === 0) {
    return null;
  }

  if (cartRequestsLoading) {
    return (
      <div className={S.requestStatus}>
        <Text variation="body-small" color="black-400">
          Loading request status...
        </Text>
      </div>
    );
  }
  return (
    <div className={S.requestStatusContainer}>
      <Text variation="body-small" bold className={S.sectionTitle}>
        Request Status
      </Text>
      {cartRequests.map((request) => (
        <div key={request.id} className={S.requestItem}>
          <div className={S.requestHeader}>
            <Text variation="body-small" bold>
              {request.type === 'refund' ? 'Cancellation Request' : 'Transfer Request'}
            </Text>
            <div className={c(S.statusBadge, S[`status-${request.status}`])}>
              <Text variation="caption" color="white-100">
                {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
              </Text>
            </div>
          </div>
          <Text variation="body-small" color="black-400">
            Reason: {request.reason}
          </Text>
          {request.admin_notes && (
            <Text variation="body-small" color="black-400">
              Admin Notes: {request.admin_notes}
            </Text>
          )}
          {request.target_event && (
            <Text variation="body-small" color="black-400">
              Target Event: {request.target_event.title}
            </Text>
          )}
          <Text variation="caption" color="black-400">
            Requested: {moment(request.created_at).format('DD MMM YYYY')}
          </Text>
        </div>
      ))}
    </div>
  );
};

// QR Code carousel template
const QrCodeTemplate: FC<{ ticket: Ticket }> = ({ ticket }) => {
  return (
    <div className={S.qrCodeCard}>
      <div className={S.attendeeBadge}>
        <i className="pi pi-user" />
        <Text bold>
          {ticket.first_name} {ticket.last_name}
        </Text>
      </div>

      <div className={S.qrCodeContainer}>
        <div className={S.scanCorners}>
          <div className={S.corner} style={{ top: 0, left: 0 }} />
          <div className={S.corner} style={{ top: 0, right: 0 }} />
          <div className={S.corner} style={{ bottom: 0, left: 0 }} />
          <div className={S.corner} style={{ bottom: 0, right: 0 }} />
        </div>
        <img src={ticket.qrcode} alt="Ticket QR Code" className={S.qrCodeImage} />
      </div>

      <Text color="black-400" className={S.scanInstructions}>
        <i className="pi pi-qrcode" style={{ marginRight: '8px' }} />
        Scan this code at the entrance
      </Text>

      {/* Wallet Buttons */}
      {(ticket.apple_wallet_link || ticket.google_wallet_link) && (
        <div className={S.dialogWalletButtons}>
          {ticket.google_wallet_link && (
            <>
              <Tooltip
                target=".dialog-google-wallet"
                content="Add to Google Wallet"
                position="top"
              />
              <a
                href={ticket.google_wallet_link}
                target="_blank"
                rel="noopener noreferrer"
                className={`${S.dialogWalletButton} dialog-google-wallet`}
              >
                <img src={playStoreIcon} alt="Google Wallet" />
                <span>Add to Google Wallet</span>
              </a>
            </>
          )}
          {ticket.apple_wallet_link && (
            <>
              <Tooltip target=".dialog-apple-wallet" content="Add to Apple Wallet" position="top" />
              <a
                href={ticket.apple_wallet_link}
                target="_blank"
                rel="noopener noreferrer"
                className={`${S.dialogWalletButton} dialog-apple-wallet`}
              >
                <img src={appleStoreIcon} alt="Apple Wallet" />
                <span>Add to Apple Wallet</span>
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const AttendeeEventsSidebar = () => {
  const [showQrCodeDialog, setShowQrCodeDialog] = useState(false);
  const [orderDetailsExpanded, setOrderDetailsExpanded] = useState(false);
  const showSidebar = useAppSelector(showAttendeeEventsSidebarSelector);
  const ticketInfo = useAppSelector(currentAttendeeEventsSidebarSelector);
  const cartRequests = useAppSelector(cartRequestsSelector);
  const cartRequestsLoading = useAppSelector(cartRequestsLoadingSelector);
  const dispatch = useAppDispatch();
  const actions = attendeeEventsSidebarActions;
  const history = useHistory();
  const location = useLocation();

  useCheckAttendeeEventsSidebarUrl();
  useEffect(() => {
    if (showSidebar && ticketInfo?.cart_id && !cartRequests) {
      dispatch(fetchCartRequests(ticketInfo.cart_id));
    }
  }, [showSidebar, ticketInfo?.cart_id, dispatch]);

  const {
    event,
    cart_id,
    friendly_cart_id,
    purchase_date,
    total,
    refund_amount,
    tickets,
    payment_method,
    card_type,
    last4,
    status,
  } = ticketInfo || {};

  // Check for pending requests
  const pendingRequests = cartRequests?.filter((request) => request.status === 'pending') || [];
  const hasPendingRequests = pendingRequests.length > 0;

  // Check if user can access tickets/documents (only attending or checkedIn status)
  const canAccessTickets = status === 'attending' || status === 'checkedIn';

  // Check if user can cancel or transfer (only attending status)
  const canCancelOrTransfer = status === 'attending';

  // Format purchase date
  const purchaseDate = moment(purchase_date).format('dddd, D MMMM YYYY h:mm A');

  // Format event date and time from start_date and end_date
  const startDate = moment(event?.start_date).tz(event?.timezone);
  const endDate = moment(event?.end_date).tz(event?.timezone);

  // Format date part
  let displayDate = '';

  // If start and end dates are on the same day
  if (startDate?.format('YYYY-MM-DD') === endDate?.format('YYYY-MM-DD')) {
    displayDate = startDate?.format('D MMM YYYY');
  }
  // If start and end dates are in the same month and year
  else if (startDate?.format('MMM YYYY') === endDate?.format('MMM YYYY')) {
    displayDate = `${startDate?.format('D')} - ${endDate?.format('D MMM YYYY')}`;
  }
  // If start and end dates are in different months but same year
  else if (startDate?.format('YYYY') === endDate?.format('YYYY')) {
    displayDate = `${startDate?.format('D MMM')} - ${endDate?.format('D MMM YYYY')}`;
  }
  // If start and end dates are in different years
  else {
    displayDate = `${startDate?.format('D MMM YYYY')} - ${endDate?.format('D MMM YYYY')}`;
  }

  // Format time part
  const startTime = startDate?.format('h:mm A');
  const endTime = endDate?.format('h:mm A');
  const displayTime = `${startTime} - ${endTime}`;

  // Get payment logo
  const paymentLogoComponent = getPaymentLogo(card_type?.toLowerCase() || '');

  // Order status tag class
  const hasRefunds = refund_amount > 0;
  const classNames = c(styles.badge, {
    [styles.refunded]: hasRefunds,
    [styles.success]: !hasRefunds,
  });

  // Handle cancel request
  const handleCancel = async (data: { refund: boolean; reason: string }) => {
    try {
      await dispatch(
        cancelAndRefundAttendee({
          cart_id,
          requestRefund: data.refund,
          reason: data.reason,
        }),
      );

      // Show success message based on refund request
      const message = data.refund
        ? 'You have been marked as not attending and a refund request has been sent to the host for review.'
        : 'You have been successfully marked as not attending.';

      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'success',
          detail: message,
          life: 5000,
        }),
      );
    } catch (error) {
      console.error('Cancel failed:', error);
      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'error',
          detail: 'Failed to process cancellation. Please try again.',
          life: 5000,
        }),
      );
    }
  };

  // Handle transfer request
  const handleTransfer = async (data: {
    eventId: string;
    occurrenceId?: string;
    reason: string;
  }) => {
    try {
      await dispatch(
        transferAttendeeTicket({
          cart_id,
          targetEventId: data.eventId,
          reason: data.reason,
        }),
      );

      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'success',
          detail:
            'Your transfer request has been sent to the host for approval. You will be notified of their decision.',
          life: 5000,
        }),
      );
    } catch (error) {
      console.error('Transfer failed:', error);
      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'error',
          detail: 'Failed to submit transfer request. Please try again.',
          life: 5000,
        }),
      );
    }
  };

  // Open cancel/transfer modal
  const openCancelTransferModal = () => {
    dispatch(
      setCurrentModalAction({
        currentModal: CONNECTED_MODALS.CANCEL_TRANSFER_MODAL,
        data: {
          title: 'Cancel or Transfer Event',
          ticketInfo,
          onCancel: handleCancel,
          onTransfer: handleTransfer,
        },
      }),
    );
  };

  // Open contact host modal
  const openContactHostModal = () => {
    dispatch(
      setCurrentModalAction({
        currentModal: CONNECTED_MODALS.CONTACT_HOST_MODAL,
        data: {
          title: 'Contact Host',
          ticketInfo,
        },
      }),
    );
  };

  const handleOnCloseSidebar = useCallback(() => {
    const basePath = location.pathname.split('/ticket/')[0];
    history.push(basePath);
    dispatch(actions.hideAttendeeEventsSidebar());
  }, []);

  if (!ticketInfo) return null;
  return (
    <>
      <Dialog
        header={
          <div className={S.dialogHeader}>
            <i className="pi pi-ticket" style={{ fontSize: '1.5rem' }} />
            <span>View QR Code</span>
            {tickets.length > 1 && (
              <span className={S.ticketCount}>
                {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        }
        visible={showQrCodeDialog}
        onHide={() => setShowQrCodeDialog(false)}
        className={S.qrCodeDialog}
        style={{ width: '90%', maxWidth: '500px' }}
        draggable={false}
        resizable={false}
        closeOnEscape
      >
        {tickets.length > 0 &&
          (tickets.length === 1 ? (
            <QrCodeTemplate ticket={tickets[0]} />
          ) : (
            <Carousel
              value={tickets}
              numVisible={1}
              numScroll={1}
              itemTemplate={(ticket) => <QrCodeTemplate ticket={ticket} />}
              showIndicators
              showNavigators={true}
              circular
              className={S.qrCarousel}
            />
          ))}
      </Dialog>

      <Sidebar
        pt={pt}
        visible={showSidebar}
        header="Event Details"
        position="right"
        onHide={handleOnCloseSidebar}
      >
        {/* ORDER DETAILS ACCORDION */}
        <div className={S.buttonSection}>
          <div
            className={S.accordionHeader}
            onClick={() => setOrderDetailsExpanded(!orderDetailsExpanded)}
          >
            <div className={S.accordionHeaderLeft}>
              <i className="pi pi-shopping-cart" style={{ fontSize: '1rem', color: '#9c26dc' }} />
              <div>
                <Text variation="body-small" bold color="black-400">
                  ORDER DETAILS
                </Text>
                <Text variation="caption" color="black-400">
                  #{friendly_cart_id}
                </Text>
              </div>
            </div>
            <div className={S.accordionHeaderRight}>
              <Tag
                className={classNames}
                severity={hasRefunds ? 'warning' : 'success'}
                value={hasRefunds ? 'Partial' : 'Success'}
                style={{ fontSize: '0.7rem', padding: '2px 8px' }}
              />
              <i
                className={`pi pi-chevron-down ${orderDetailsExpanded ? S.chevronExpanded : ''}`}
                style={{ fontSize: '0.875rem', color: '#9c26dc', marginLeft: '8px' }}
              />
            </div>
          </div>

          {orderDetailsExpanded && (
            <div className={S.accordionContent}>
              <div className={S.ticketInfoWrapper}>
                <div className={S.infoRow}>
                  <div className={S.rowTitle}>
                    <Text variation="body-small" color="black-100">
                      Purchased Date
                    </Text>
                    <Text variation="h5">{purchaseDate}</Text>
                  </div>
                </div>
                <div className={S.infoRow}>
                  <div className={S.rowTitle}>
                    <Text variation="body-small" color="black-100">
                      Tickets Purchased
                    </Text>
                    <Text variation="h5">
                      {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
                    </Text>
                  </div>
                  <Divider layout="vertical" className={S.divider} />
                  <div className={S.rowTitle}>
                    <Text variation="body-small" color="black-100">
                      Total
                    </Text>
                    <Text variation="h5">
                      {ticketInfo.currency_symbol}
                      {total.toFixed(2)}
                    </Text>
                  </div>
                  <Divider layout="vertical" className={S.divider} />
                  <div className={S.rowTitle}>
                    <Text variation="body-small" color="black-100">
                      Refunds
                    </Text>
                    <Text variation="h5">
                      {ticketInfo.currency_symbol}
                      {refund_amount.toFixed(2)}
                    </Text>
                  </div>
                </div>
                {payment_method !== 'N/A' && (
                  <div className={S.infoRow}>
                    <div className={S.rowTitle}>
                      <Text variation="body-small" color="black-100">
                        Payment Details
                      </Text>
                      <div className={S.paymentCardWrapper}>
                        {paymentLogoComponent && (
                          <div className={S.paymentLogo}>{paymentLogoComponent}</div>
                        )}
                        {card_type !== 'N/A'
                          ? `${card_type} (${last4 ? `** ${last4}` : ''})`
                          : payment_method}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <RequestStatus />
            </div>
          )}
        </div>
        {/* TICKETS SECTION - Only visible for attending/checkedIn status */}
        {canAccessTickets && (
          <div className={S.buttonSection}>
            <div className={S.sectionHeader}>
              <i className="pi pi-ticket" style={{ fontSize: '0.875rem', color: '#9c26dc' }} />
              <Text variation="body-small" bold color="black-400">
                TICKETS
              </Text>
            </div>
            {tickets.some((ticket) => ticket.qrcode) && (
              <RedesignButton
                variation="primary"
                height={52}
                className={S.fullWidthButton}
                onClick={() => setShowQrCodeDialog(true)}
              >
                <TicketIcon />
                <Text regular color="white-100">
                  View QR Code
                </Text>
              </RedesignButton>
            )}
          </div>
        )}

        {/* DOCUMENTS SECTION - Only visible for attending/checkedIn status */}
        {canAccessTickets && (
          <div className={S.buttonSection}>
            <div className={S.sectionHeader}>
              <i className="pi pi-file" style={{ fontSize: '0.875rem', color: '#9c26dc' }} />
              <Text variation="body-small" bold color="black-400">
                DOCUMENTS
              </Text>
            </div>
            <div className={S.documentButtons}>
              <RedesignButton
                variation="secondary"
                height={52}
                onClick={() => dispatch(fetchTicketReceiptPdf(cart_id))}
              >
                <EyeIcon />
                <Text regular color="brand-400">
                  View Receipt
                </Text>
              </RedesignButton>
              <RedesignButton
                variation="secondary"
                height={52}
                onClick={() => dispatch(fetchTicketPdf(cart_id))}
              >
                <DownloadIcon />
                <Text regular color="brand-400">
                  Download PDF
                </Text>
              </RedesignButton>
            </div>
          </div>
        )}

        {/* ACTIONS SECTION */}
        <div className={S.buttonSection}>
          <div className={S.sectionHeader}>
            <i className="pi pi-cog" style={{ fontSize: '0.875rem', color: '#9c26dc' }} />
            <Text variation="body-small" bold color="black-400">
              ACTIONS
            </Text>
          </div>
          <div className={S.actionLinks}>
            {/* Cancel or Transfer - Only visible for attending status */}
            {canCancelOrTransfer && (
              <div
                onClick={hasPendingRequests ? undefined : openCancelTransferModal}
                className={hasPendingRequests ? S.disabledLink : S.actionLink}
              >
                <i className="pi pi-refresh" />
                <Text regular color={hasPendingRequests ? 'black-400' : 'brand-400'}>
                  {hasPendingRequests ? 'Request Pending' : 'Cancel or Transfer'}
                </Text>
              </div>
            )}
            <div onClick={openContactHostModal} className={S.actionLink}>
              <i className="pi pi-envelope" />
              <Text regular color="brand-400">
                Contact Host
              </Text>
            </div>
          </div>
        </div>
      </Sidebar>
    </>
  );
};
